// useWebRTC.js — WebRTC hook: voice, video, screen share
//
// Signaling flow:
//   Joiner        → voice_join → server
//   Server        → voice_peers (existing list) → joiner
//   Joiner        → rtc_offer → each existing peer
//   Existing peer → rtc_answer → joiner
//   Server        → voice_peer_joined → existing peers (when new joiner arrives)
//   Existing peer → rtc_offer → new joiner
//   New joiner    → rtc_answer → existing peer
//   Both sides exchange ICE candidates throughout
import { useRef, useState, useCallback } from "react";
import { rtcLog } from "./components/CallPanel";

// Device fingerprint — captured once per page load
const getDeviceInfo = () => ({
  ua:       navigator.userAgent,
  platform: navigator.platform,
  touch:    navigator.maxTouchPoints,
  cores:    navigator.hardwareConcurrency,
  memory:   navigator.deviceMemory || null,
  screen:   `${window.screen.width}×${window.screen.height}@${window.devicePixelRatio}x`,
  viewport: `${window.innerWidth}×${window.innerHeight}`,
  network:  navigator.connection ? `${navigator.connection.effectiveType} ${navigator.connection.downlink}Mbps` : null,
  isIos:    /iP(hone|ad|od)/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1),
  isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
});

export default function useWebRTC(socket) {
  const [peers, setPeers]               = useState({});
  const [localStream, setLocalStream]   = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [muted, setMuted]               = useState(false);
  const [camOff, setCamOff]             = useState(false);
  const [sharing, setSharing]           = useState(false);

  const peerConns    = useRef({});
  const icePending   = useRef({});
  const localRef     = useRef(null);
  const iceRef       = useRef([{ urls: "stun:stun.l.google.com:19302" }]);
  const channelRef   = useRef(null);
  const deviceInfo   = useRef(null);  // captured at join time

  // Emit to both local log panel and server (for admin view)
  const emit = useCallback((msg, type = 'info') => {
    console.log(`[rtc] ${msg}`);
    rtcLog.push(msg, type);
    if (socket && channelRef.current) {
      socket.emit('rtc_log', {
        msg, type,
        channel: channelRef.current,
        deviceInfo: deviceInfo.current,
      });
    }
  }, [socket]);

  // ── Load TURN credentials ──────────────────────────────────────────────────
  const loadIce = async () => {
    try {
      const base = process.env.REACT_APP_SERVER_URL || "";
      const data = await fetch(`${base}/turn-credentials`).then(r => r.json());
      if (data.iceServers?.length) iceRef.current = data.iceServers;
    } catch {}
    return iceRef.current;
  };

  // ── Drain buffered ICE after remoteDescription is set ─────────────────────
  const drainIce = async (peerId, pc) => {
    const buf = icePending.current[peerId] || [];
    icePending.current[peerId] = [];
    for (const c of buf) {
      try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch {}
    }
  };

  // ── Build a new RTCPeerConnection ──────────────────────────────────────────
  const makePeer = useCallback((peerId, info) => {
    if (peerConns.current[peerId]) {
      const pc = peerConns.current[peerId];
      const stream = localRef.current;
      if (stream) {
        const senders = pc.getSenders();
        stream.getTracks().forEach(t => {
          const alreadySending = senders.some(s => s.track?.kind === t.kind);
          if (!alreadySending) {
            pc.addTrack(t, stream);
            emit(`late-added ${t.kind} track to existing peer ${info?.username || peerId.slice(0,8)}`, 'warn');
          }
        });
      }
      return pc;
    }

    emit(`creating peer conn → ${info?.username || peerId.slice(0,8)}`);
    const pc = new RTCPeerConnection({ iceServers: iceRef.current });
    peerConns.current[peerId] = pc;
    icePending.current[peerId] = [];

    const stream = localRef.current;
    if (stream) {
      stream.getTracks().forEach(t => {
        pc.addTrack(t, stream);
        emit(`  added local ${t.kind} track (${t.label.slice(0,30)}) to PC`);
      });
    } else {
      emit('  no local stream when creating peer', 'warn');
    }

    // Always add a recvonly video transceiver when we have no video track.
    // Without this an audio-only joiner can't receive peer video until the
    // peer does a full renegotiation — causing the 6 second black-video delay.
    const hasVideoTrack = stream?.getVideoTracks().length > 0;
    if (!hasVideoTrack) {
      try {
        pc.addTransceiver('video', { direction: 'recvonly' });
        emit(`  added recvonly video transceiver (audio-only mode)`);
      } catch (e) { emit(`  addTransceiver failed: ${e.message}`, 'warn'); }
    }

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        emit(`  → ICE ${candidate.type} → ${info?.username || peerId.slice(0,8)}`);
        socket.emit("rtc_ice", { to: peerId, candidate });
      }
    };

    pc.oniceconnectionstatechange = () => {
      const s = pc.iceConnectionState;
      emit(`ICE state [${info?.username || peerId.slice(0,8)}]: ${s}`, s === 'connected' || s === 'completed' ? 'ok' : s === 'failed' || s === 'disconnected' ? 'err' : 'info');
    };

    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      emit(`conn state [${info?.username || peerId.slice(0,8)}]: ${s}`, s === 'connected' ? 'ok' : s === 'failed' || s === 'disconnected' ? 'err' : 'info');
      if (["disconnected","failed","closed"].includes(s)) dropPeer(peerId);
    };

    pc.onsignalingstatechange = () => {
      emit(`signaling [${info?.username || peerId.slice(0,8)}]: ${pc.signalingState}`);
    };

    let peerStream = new MediaStream();

    pc.ontrack = ({ track, streams }) => {
      emit(`ontrack ← ${info?.username || peerId.slice(0,8)}: ${track.kind} readyState=${track.readyState} muted=${track.muted}`, 'ok');

      if (track.kind === 'video') {
        peerStream = new MediaStream([...peerStream.getTracks(), track]);
      } else {
        peerStream.addTrack(track);
      }

      const captured = peerStream;
      const push = () => setPeers(p => ({ ...p, [peerId]: { ...p[peerId], ...info, stream: captured, _t: Date.now() } }));
      push();

      track.onunmute = () => {
        emit(`  track unmuted: ${track.kind} [${info?.username || peerId.slice(0,8)}]`, 'ok');
        const refreshed = new MediaStream(captured.getTracks());
        peerStream = refreshed;
        setPeers(p => ({ ...p, [peerId]: { ...p[peerId], ...info, stream: refreshed, _t: Date.now() } }));
      };

      track.onmute = () => {
        emit(`  track muted: ${track.kind} [${info?.username || peerId.slice(0,8)}]`, 'warn');
      };

      track.onended = () => {
        emit(`  track ended: ${track.kind} [${info?.username || peerId.slice(0,8)}]`, 'warn');
      };

      if (track.kind === 'video') {
        let tries = 0;
        const poll = setInterval(() => {
          push();
          if (++tries >= 10) clearInterval(poll);
        }, 500);
        track.addEventListener('ended', () => clearInterval(poll));
      }
    };

    setPeers(p => ({ ...p, [peerId]: { ...info, stream: null } }));
    return pc;
  }, [socket, emit]); // eslint-disable-line

  const dropPeer = useCallback((peerId) => {
    peerConns.current[peerId]?.close();
    delete peerConns.current[peerId];
    delete icePending.current[peerId];
    setPeers(p => { const n = { ...p }; delete n[peerId]; return n; });
  }, []);

  // ── Register all WebRTC socket listeners ───────────────────────────────────
  const attachListeners = useCallback((channelId) => {
    // Remove any stale listeners first
    ["voice_peers","voice_peer_joined","rtc_offer","rtc_answer","rtc_ice","voice_peer_left"]
      .forEach(e => socket.off(e));

    // We just joined — server tells us who's already here.
    // WE are the offerer for each existing peer (they will answer).
    socket.on("voice_peers", async ({ peers: existing }) => {
      emit(`voice_peers: ${existing.length} peer(s) in channel`);
      for (const peer of existing) {
        if (peer.socketId === socket.id) continue;
        emit(`  sending offer → ${peer.username}`);
        const pc = makePeer(peer.socketId, { username: peer.username, avatar: peer.avatar, userId: peer.userId });
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          socket.emit("rtc_offer", { to: peer.socketId, offer, channelId });
          emit(`  offer sent → ${peer.username}`, 'ok');
        } catch (e) { emit(`createOffer failed: ${e.message}`, 'err'); }
      }
    });

    socket.on("voice_peer_joined", ({ peerId, username, avatar, userId }) => {
      if (peerId === socket.id) return;
      emit(`peer joined: ${username} — awaiting their offer`);
      makePeer(peerId, { username, avatar, userId });
    });

    socket.on("rtc_offer", async ({ from, offer, username, avatar, userId }) => {
      emit(`← offer from ${username || from.slice(0,8)}`);
      const pc = makePeer(from, { username, avatar, userId });
      try {
        if (pc.signalingState === "have-local-offer") {
          emit(`  rollback local offer (renegotiation)`, 'warn');
          await pc.setLocalDescription({ type: "rollback" });
        }
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        await drainIce(from, pc);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit("rtc_answer", { to: from, answer, channelId });
        emit(`  answer sent → ${username || from.slice(0,8)}`, 'ok');
      } catch (e) { emit(`answer failed: ${e.message}`, 'err'); }
    });

    socket.on("rtc_answer", async ({ from, answer }) => {
      const pc = peerConns.current[from];
      if (!pc) return;
      emit(`← answer from ${Object.values(peerConns.current).length} peer(s) known`);
      try {
        if (pc.signalingState === "have-local-offer") {
          await pc.setRemoteDescription(new RTCSessionDescription(answer));
          await drainIce(from, pc);
          emit(`  remote desc set, draining ICE`, 'ok');
        } else {
          emit(`  unexpected signaling state for answer: ${pc.signalingState}`, 'warn');
        }
      } catch (e) { emit(`setRemoteDesc(answer) failed: ${e.message}`, 'err'); }
    });

    socket.on("rtc_ice", async ({ from, candidate }) => {
      const pc = peerConns.current[from];
      if (!pc) return;
      if (pc.remoteDescription) {
        try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); }
        catch (e) { emit(`addIceCandidate failed: ${e.message}`, 'warn'); }
      } else {
        if (!icePending.current[from]) icePending.current[from] = [];
        icePending.current[from].push(candidate);
        emit(`  ICE buffered (no remote desc yet) for ${from.slice(0,8)}`);
      }
    });

    socket.on("voice_peer_left", ({ peerId }) => {
      const name = peers[peerId]?.username || peerId.slice(0,8);
      emit(`peer left: ${name}`, 'warn');
      dropPeer(peerId);
    });

  }, [socket, emit, makePeer, dropPeer]); // eslint-disable-line

  const deviceIds = useRef({ audio: undefined, video: undefined });

  // ── Switch audio/video device mid-call ────────────────────────────────────
  const switchDevice = useCallback(async (kind, deviceId) => {
    deviceIds.current[kind] = deviceId;
    const stream = localRef.current;
    if (!stream || !channelRef.current) return; // not in a call, just save for next join

    const constraints = kind === 'audio'
      ? { audio: { deviceId: { exact: deviceId } } }
      : { video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } } };

    try {
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      const newTrack = newStream.getTracks()[0];
      emit(`switchDevice ${kind}: ${newTrack.label.slice(0,30)}`, 'ok');
      for (const pc of Object.values(peerConns.current)) {
        const sender = pc.getSenders().find(s => s.track?.kind === kind);
        if (sender) await sender.replaceTrack(newTrack);
      }
      stream.getTracks().filter(t => t.kind === kind).forEach(t => { t.stop(); stream.removeTrack(t); });
      stream.addTrack(newTrack);
      setLocalStream(s => s);
    } catch (e) { emit(`switchDevice(${kind}) failed: ${e.message}`, 'err'); }
  }, []);

  // ── Join a voice/video channel ─────────────────────────────────────────────
  const joinChannel = useCallback(async (channelId, { video = false } = {}) => {
    rtcLog.clear();
    deviceInfo.current = getDeviceInfo();
    emit(`--- JOIN: ${channelId} video=${video} ---`);
    emit(`Device: ${deviceInfo.current.isIos ? 'iOS' : deviceInfo.current.isSafari ? 'Safari' : 'Other'} | ${deviceInfo.current.platform} | touch=${deviceInfo.current.touch} | ${deviceInfo.current.screen}`);
    emit(`UA: ${navigator.userAgent}`);
    if (deviceInfo.current.network) emit(`Network: ${deviceInfo.current.network}`);

    const prev = channelRef.current;
    if (prev) {
      emit(`leaving prev channel: ${prev}`, 'warn');
      socket.emit("voice_leave", { channelId: prev });
      localRef.current?.getTracks().forEach(t => t.stop());
      Object.values(peerConns.current).forEach(pc => pc.close());
      peerConns.current = {};
      icePending.current = {};
      setPeers({});
      setLocalStream(null);
    }
    channelRef.current = channelId;

    const iceServers = await loadIce();
    emit(`ICE servers: ${JSON.stringify(iceServers.map(s => s.urls))}`);

    const audioConstraint = deviceIds.current.audio ? { deviceId: { exact: deviceIds.current.audio } } : true;
    const videoConstraint = video
      ? (deviceIds.current.video ? { deviceId: { exact: deviceIds.current.video }, width: { ideal: 1280 }, height: { ideal: 720 } } : { width: { ideal: 1280 }, height: { ideal: 720 } })
      : false;

    emit(`requesting media: audio=${JSON.stringify(audioConstraint)} video=${JSON.stringify(videoConstraint)}`);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraint, video: videoConstraint });
      emit(`got media: ${stream.getTracks().map(t => `${t.kind}:${t.label.slice(0,25)}`).join(', ')}`, 'ok');
    } catch (e) {
      emit(`media failed (${e.name}): ${e.message} — trying audio-only`, 'warn');
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        emit(`audio-only fallback OK`, 'warn');
      } catch (e2) {
        emit(`no media access: ${e2.message}`, 'err');
        return;
      }
    }

    localRef.current = stream;
    setLocalStream(stream);
    setCamOff(!video || stream.getVideoTracks().length === 0);

    attachListeners(channelId);
    socket.emit("voice_join", { channelId });
    emit(`voice_join emitted`, 'ok');

  }, [socket, attachListeners]);

  // ── Leave channel ──────────────────────────────────────────────────────────
  const leaveChannel = useCallback(() => {
    const channelId = channelRef.current;
    emit(`--- LEAVE CHANNEL: ${channelId} ---`, 'warn');
    if (channelId) socket?.emit("voice_leave", { channelId });
    channelRef.current = null;

    localRef.current?.getTracks().forEach(t => t.stop());
    localRef.current = null;
    screenStream?.getTracks().forEach(t => t.stop());

    setLocalStream(null);
    setScreenStream(null);
    setSharing(false);
    setMuted(false);
    setCamOff(false);

    Object.values(peerConns.current).forEach(pc => pc.close());
    peerConns.current = {};
    icePending.current = {};
    setPeers({});

    ["voice_peers","voice_peer_joined","rtc_offer","rtc_answer","rtc_ice","voice_peer_left"]
      .forEach(e => socket?.off(e));
  }, [socket, screenStream]);

  const toggleMute = useCallback(() => {
    const stream = localRef.current;
    if (!stream) return;
    const next = !stream.getAudioTracks()[0]?.enabled;
    stream.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setMuted(m => !m);
    emit(`mic ${next ? 'unmuted' : 'muted'}`, 'info');
  }, []);

  const toggleCamera = useCallback(async () => {
    const stream = localRef.current;
    if (!stream) return;
    const vt = stream.getVideoTracks();
    if (vt.length > 0) {
      const next = !vt[0].enabled;
      vt.forEach(t => { t.enabled = !t.enabled; });
      setCamOff(c => !c);
      emit(`camera ${next ? 'on' : 'off'}`, 'info');
    } else {
      emit(`acquiring camera (no video track yet)...`, 'info');
      try {
        const vs = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } } });
        const track = vs.getVideoTracks()[0];
        emit(`camera acquired: ${track.label}`, 'ok');
        stream.addTrack(track);
        const refreshed = new MediaStream(stream.getTracks());
        localRef.current = refreshed;
        setLocalStream(refreshed);
        setCamOff(false);

        for (const [peerId, pc] of Object.entries(peerConns.current)) {
          try {
            const sender = pc.getSenders().find(s => s.track?.kind === "video");
            if (sender) {
              await sender.replaceTrack(track);
              emit(`  replaceTrack(video) → ${peerId.slice(0,8)}`, 'ok');
            } else {
              pc.addTrack(track, stream);
              const offer = await pc.createOffer();
              await pc.setLocalDescription(offer);
              socket.emit("rtc_offer", { to: peerId, offer, channelId: channelRef.current });
              emit(`  renegotiated video → ${peerId.slice(0,8)}`, 'ok');
            }
          } catch (e) { emit(`renegotiate failed for ${peerId.slice(0,8)}: ${e.message}`, 'err'); }
        }
      } catch (e) { emit(`camera error: ${e.name} ${e.message}`, 'err'); }
    }
  }, [socket, emit]); // eslint-disable-line

  // ── Screen share toggle ────────────────────────────────────────────────────
  const toggleScreenShare = useCallback(async () => {
    if (sharing) {
      screenStream?.getTracks().forEach(t => t.stop());
      setScreenStream(null);
      setSharing(false);
      const stream = localRef.current;
      if (stream) {
        const camTrack = stream.getVideoTracks()[0];
        Object.values(peerConns.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track?.kind === "video");
          if (sender && camTrack) sender.replaceTrack(camTrack);
        });
      }
    } else {
      try {
        const ss = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        const st = ss.getVideoTracks()[0];
        setScreenStream(ss);
        setSharing(true);
        st.onended = () => toggleScreenShare();
        Object.values(peerConns.current).forEach(pc => {
          const sender = pc.getSenders().find(s => s.track?.kind === "video");
          if (sender) sender.replaceTrack(st);
        });
      } catch (e) { console.error("[rtc] screen share error", e); }
    }
  }, [sharing, screenStream]);

  // ── Adaptive bitrate — call when tile size changes ────────────────────────
  const setVideoBitrate = useCallback((maxKbps) => {
    Object.values(peerConns.current).forEach(async pc => {
      for (const sender of pc.getSenders()) {
        if (sender.track?.kind !== 'video') continue;
        try {
          const params = sender.getParameters();
          if (!params.encodings?.length) params.encodings = [{}];
          params.encodings.forEach(e => { e.maxBitrate = maxKbps * 1000; });
          await sender.setParameters(params);
        } catch {}
      }
    });
  }, []);

  // ── Mic gain (0.0 – 2.0, default 1.0) ────────────────────────────────────
  const micGainNode = useRef(null);
  const micGainCtx  = useRef(null);
  const [micGain, setMicGainState] = useState(1.0);

  const setMicGain = useCallback((value) => {
    const gain = Math.max(0, Math.min(2, value));
    setMicGainState(gain);
    if (micGainNode.current) {
      micGainNode.current.gain.setTargetAtTime(gain, micGainCtx.current.currentTime, 0.01);
      return;
    }
    // Set up gain node on local stream
    const stream = localRef.current;
    if (!stream) return;
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks.length) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const gainNode = ctx.createGain();
      const dst = ctx.createMediaStreamDestination();
      gainNode.gain.value = gain;
      src.connect(gainNode);
      gainNode.connect(dst);
      micGainNode.current = gainNode;
      micGainCtx.current  = ctx;
      // Replace audio track in all peer connections with processed track
      const processedTrack = dst.stream.getAudioTracks()[0];
      Object.values(peerConns.current).forEach(pc => {
        const sender = pc.getSenders().find(s => s.track?.kind === 'audio');
        if (sender) sender.replaceTrack(processedTrack).catch(() => {});
      });
    } catch (e) { emit(`mic gain error: ${e.message}`, 'err'); }
  }, [emit]);

  // ── Per-peer volume — stores volumes, VideoTile applies them via ref ───────
  const [peerVolumes, setPeerVolumes] = useState({}); // peerId → 0.0-1.0
  const setPeerVolume = useCallback((peerId, volume) => {
    const v = Math.max(0, Math.min(1, volume));
    setPeerVolumes(prev => ({ ...prev, [peerId]: v }));
  }, []);

  return {
    peers, localStream, screenStream,
    muted, camOff, sharing,
    micGain, setMicGain,
    peerVolumes, setPeerVolume,
    joinChannel, leaveChannel,
    toggleMute, toggleCamera, toggleScreenShare,
    setVideoBitrate, switchDevice,
    _peerConns: peerConns,
  };
}
